import React, {FC, useEffect} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {FrontEvent, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {InterfaceEventsEnum} from '../../../../services/tracking';
import {getUnsavedViewNavigationBlockerId} from '../../../../store/panels/analytics/analyticsPanelSelectors';
import {getAnalyticsParameters} from '../../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {
  addTemporaryViewAction,
  setSelectedViewAction,
  toggleViewVisibility,
} from '../../../../store/panels/analytics/views/analyticsPanelViewsActions';
import {
  getAnalyticsAllViews,
  getAnalyticsNbTemporaryViews,
  getAnalyticsTemporaryViews,
  makeGetSelectedView,
} from '../../../../store/panels/analytics/views/analyticsPanelViewsSelectors';
import {
  ALL_DATA_ID,
  AnalyticsSavedView,
  AnalyticsSelectedViewId,
  isAnalyticsViewSaved,
  isAnalyticsViewTemporary,
  TEMPORARY_VIEW_ID_PREFIX,
} from '../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {makeAnalyticsSelectedViewUrl} from '../../../helpers/analyticsUrlHelpers';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {MAX_REPORT_PANEL_WIDTH} from '../../reportViewer/reportCommons';
import {TabButtonElement} from '../../tabs/tabButtonElement';
import {StyledTabElementsWrapper} from '../../tabs/tabCommon';
import {TabPinIcon} from '../../tabs/tabPinIcon';
import {SavedViewsDropdown} from './reportHeaderSavedViewsDropdown';

/*
 * Props.
 */

interface ReportHeaderTabsProps {
  selectedViewId: AnalyticsSelectedViewId;
}

/*
 * Styles.
 */

const StyledTabsWrapper = styled.div`
  display: flex;
  justify-content: space-between;

  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledReportHeaderTabElementsWrapper = styled(StyledTabElementsWrapper)`
  flex: 1;

  max-width: ${MAX_REPORT_PANEL_WIDTH - 200}px;
  min-width: 0;
`;

/*
 * Component.
 */

export const ReportHeaderTabs: FC<ReportHeaderTabsProps> = ({selectedViewId}) => {
  const dispatch = useAppDispatch();

  const views = useAppSelector(getAnalyticsAllViews);
  const trackAnalytics = useAnalyticsTracking();
  const parameters = useAppSelector(getAnalyticsParameters);
  const currentSavedView = useAppSelector(makeGetSelectedView(selectedViewId));
  const nbTemporaryViews = useAppSelector(getAnalyticsNbTemporaryViews);
  const temporaryViews = useAppSelector(getAnalyticsTemporaryViews);
  const unsavedViewNavigationBlockerId = useAppSelector(getUnsavedViewNavigationBlockerId);

  // TODO: This useEffect hook is pretty gnarly, and contains logic that seems like it should live
  // elsewhere (potentially when we fetchSavedViews?). Probably makes sense to refactor.
  useEffect(() => {
    if (isAnalyticsViewTemporary(selectedViewId)) {
      const tempView = temporaryViews.find((v) => v.id === selectedViewId);
      if (!tempView) {
        dispatch(addTemporaryViewAction(selectedViewId));
      }
    }

    dispatch(setSelectedViewAction(selectedViewId));

    // If the selectedViewId matches no view, or a hidden view, we go back to All Data
    if (isAnalyticsViewSaved(selectedViewId)) {
      if (!currentSavedView && !isAnalyticsViewTemporary(selectedViewId)) {
        const path = makeAnalyticsSelectedViewUrl(ALL_DATA_ID, parameters);
        // Default view redirection shouldn't be blockable.
        dispatch(navigateToLinkAction(path, undefined, unsavedViewNavigationBlockerId));
      }
    }

    // TODO: change url construct to avoid passing parameters everytime we build the new url.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewId, dispatch]);

  const onTabClick = (id: AnalyticsSelectedViewId) => {
    const path = makeAnalyticsSelectedViewUrl(id, parameters);
    dispatch(navigateToLinkAction(path));
  };

  const onToggleViewVisibility = (id: AnalyticsSelectedViewId) => {
    const view = views.find((v) => v.id === id);
    const isVisible = Boolean(view?.isDisplayed);
    trackAnalytics(
      isVisible ? InterfaceEventsEnum.ANALYTICS_VIEW_CLOSE : InterfaceEventsEnum.ANALYTICS_VIEW_OPEN,
      {
        saved_view_id: id,
      },
    );
    const path = makeAnalyticsSelectedViewUrl(id, parameters);
    dispatch(navigateToLinkAction(path));
  };

  const addNewTempView = () => {
    trackAnalytics(InterfaceEventsEnum.ANALYTICS_ADD_VIEW_CLICK);
    const viewId = `${TEMPORARY_VIEW_ID_PREFIX}_${nbTemporaryViews}`;
    const path = makeAnalyticsSelectedViewUrl(viewId, parameters);
    dispatch(navigateToLinkAction(path));
  };

  const onCloseTab = (id: AnalyticsSelectedViewId) => {
    if (id === selectedViewId) {
      const path = makeAnalyticsSelectedViewUrl(ALL_DATA_ID, parameters);
      dispatch(navigateToLinkAction(path));
    }
    dispatch(toggleViewVisibility(id));
  };

  return (
    <StyledTabsWrapper>
      <StyledReportHeaderTabElementsWrapper>
        {makeTabsFromViews(views, selectedViewId, onTabClick, onCloseTab)}
        <Button variant={ButtonVariantsEnum.LINK} onClick={addNewTempView}>
          <Trans id="tabs-add-view">+ Add view</Trans>
        </Button>
      </StyledReportHeaderTabElementsWrapper>
      <SavedViewsDropdown savedViews={views} onSelect={onToggleViewVisibility} />
    </StyledTabsWrapper>
  );
};

/*
 * Helpers.
 */

const makeTabsFromViews = (
  savedViews: ReadonlyArray<AnalyticsSavedView>,
  selectedViewId: AnalyticsSelectedViewId,
  onSelect: HandlerOf<AnalyticsSelectedViewId>,
  onClose: HandlerOf<AnalyticsSelectedViewId>,
) =>
  [
    {
      key: ALL_DATA_ID,
      name: <Trans id="tabs-all-data">All data</Trans>,
      isSelected: selectedViewId === ALL_DATA_ID,
      onTabClick: () => onSelect(ALL_DATA_ID),
    },
    ...savedViews
      .filter(({isDisplayed}) => isDisplayed)
      .map((view) => {
        const {id, name, isPinned} = view;
        const element = {
          key: id.toString(),
          name,
          isSelected: selectedViewId === id,
          leftIcon: isAnalyticsViewSaved(view.id) && <TabPinIcon isPinned={isPinned} view={view} />,
          onTabClick: () => onSelect(id),
        };

        return {
          ...element,
          onCloseClick: (e?: FrontEvent) => {
            // Stop propagation to avoid selecting the tab.
            if (e) {
              e.stopPropagation();
            }

            onClose(id);
          },
        };
      }),
  ].map((element) => <TabButtonElement {...element} />);
